import type {
    AnatomyBinding,
    AnatomyVariant,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import {
    pickerAnatomyParts,
    pickerAnatomyStates,
} from "./componentAnatomyPickerParts";
import { pickerSharedBindings } from "./componentAnatomyPickerBindings";

// Bindings mined from designSystem/Picker/Picker.module.scss,
// PickerElementTrigger.module.scss and PickerItem.module.scss plus the Button primary block the default Button trigger
// renders through. Parts and states live in componentAnatomyPickerParts.

const triggerFill = (token: `--${string}`): AnatomyBinding => ({
    partId: "background",
    property: "background-color",
    token,
    via: "--button-bg",
});

const triggerEdge = (token: `--${string}`): AnatomyBinding => ({
    partId: "border",
    property: "border-color",
    token,
    via: "--button-border",
});

const triggerText = (token: `--${string}`): AnatomyBinding => ({
    partId: "label",
    property: "color",
    token,
    via: "--button-fg",
});

const itemFill = (value: `--${string}` | "transparent"): AnatomyBinding =>
    value === "transparent"
        ? {
              partId: "itemBackground",
              property: "background-color",
              literal: "transparent",
          }
        : {
              partId: "itemBackground",
              property: "background-color",
              token: value,
          };

const itemText = (token: `--${string}`): AnatomyBinding => ({
    partId: "itemLabel",
    property: "color",
    token,
});

const indicatorColor = (token: `--${string}`): AnatomyBinding => ({
    partId: "indicator",
    property: "color",
    token,
    note: "The check inherits the row colour through currentColor.",
});

const focusRingBindings: readonly AnatomyBinding[] = [
    {
        partId: "focusRing",
        property: "box-shadow (spread)",
        token: "--border-width-thick",
    },
    {
        partId: "focusRing",
        property: "box-shadow (color)",
        token: "--focus-ring-color",
    },
];

const restingBindings: readonly AnatomyBinding[] = [
    triggerFill("--bg-primary"),
    triggerEdge("--border-default"),
    triggerText("--fg-primary"),
    { partId: "glyph", property: "color", token: "--fg-primary" },
    itemFill("transparent"),
    itemText("--fg-primary"),
    indicatorColor("--fg-primary"),
];

const stateBindings: AnatomyVariant["stateBindings"] = {
    hover: [
        triggerEdge("--border-hover"),
        itemFill("--bg-neutral-subtle-hover"),
    ],
    pressed: [
        triggerFill("--bg-secondary"),
        triggerEdge("--border-hover"),
        triggerText("--fg-secondary"),
        itemFill("--bg-neutral-hover"),
    ],
    focus: [triggerEdge("--border-accent-focus"), ...focusRingBindings],
    disabled: [
        triggerEdge("--border-disabled"),
        triggerText("--fg-disabled"),
        itemText("--fg-disabled"),
        indicatorColor("--fg-disabled"),
    ],
};

const flyoutVariant: AnatomyVariant = {
    id: "flyout",
    label: "Flyout row",
    selector: " .flyoutIndicator",
    restingBindings: [
        ...restingBindings,
        {
            partId: "indicator",
            property: "glyph",
            literal: "triangle",
            note: "Figma Triangle=Yes: a TriangleIcon rotated to point right, after the right text; the row keeps --bg-neutral-subtle-hover while its level is open ([data-flyout-open]).",
        },
        { partId: "indicator", property: "width", literal: "12px" },
        { partId: "indicator", property: "height", literal: "12px" },
        {
            partId: "indicator",
            property: "color",
            token: "--fg-tertiary",
            note: "Set on .flyoutIndicator; --fg-disabled under [data-disabled].",
        },
    ],
    stateBindings: {
        ...stateBindings,
        disabled: [
            ...(stateBindings.disabled ?? []),
            { partId: "indicator", property: "color", token: "--fg-disabled" },
        ],
    },
};

const variants: readonly AnatomyVariant[] = [
    {
        id: "single",
        label: "Single",
        selector: "",
        restingBindings: [
            ...restingBindings,
            {
                partId: "indicator",
                property: "glyph",
                literal: "check",
                note: "Base UI ItemIndicator mounts the CheckIcon only while the item is selected.",
            },
        ],
        stateBindings,
    },
    {
        id: "multiple",
        label: "Multiple",
        selector: " .itemCheckbox",
        restingBindings: [
            ...restingBindings,
            {
                partId: "indicator",
                property: "glyph",
                literal: "checkbox",
                note: "CheckboxIndicator painted from the row's data-selected state; its own tokens live in the Checkbox anatomy.",
            },
        ],
        stateBindings,
    },
    {
        id: "tree",
        label: "Tree row",
        selector: " .item[data-tree-position] .treeConnector",
        restingBindings: [
            ...restingBindings,
            {
                partId: "indicator",
                property: "glyph",
                literal: "check",
            },
            {
                partId: "itemBackground",
                property: "padding-left",
                literal: "calc(var(--space-8) + var(--_picker-depth) * 24px)",
                note: "24px is the Figma indent step per depth.",
            },
            { partId: "connector", property: "width", token: "--border-width" },
            {
                partId: "connector",
                property: "background-color",
                token: "--border-divider",
            },
            {
                partId: "connector",
                property: "left",
                literal:
                    "calc(16px + (var(--_picker-connector-depth) - 1) * 24px)",
                note: "From the row's left edge: the centre of the parent row's 16px leading slot (x 16 at depth 1, 40 at depth 2).",
            },
            {
                partId: "connector",
                property: "inset-block",
                literal: "top: 8px | -1px; bottom: -1px | calc(100% - 20px)",
                note: "top / middle / bottom / only variants overhang the row by half the 2px gap so sibling strokes join; ancestor lines render as extra middle bars. The last bar ends at 20px from the row top, the label baseline and 8px above a 28px row's bottom, mirroring the 8px start (Figma ends it at the 14px row centre; Daniel 2026-09-18, reported to design).",
            },
        ],
        stateBindings,
    },
    flyoutVariant,
];

export const pickerAnatomy: ComponentAnatomySpec = {
    label: "Picker",
    schematic: "picker",
    rootSelector: ".buttonTrigger + .popup",
    variantAxisLabel: "Selection",
    parts: pickerAnatomyParts,
    states: pickerAnatomyStates,
    sharedBindings: pickerSharedBindings,
    variants,
};
